import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './TutorialPage.css';
import { API_URL } from './Config';

function TutorialPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [tutorial, setTutorial] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);

  useEffect(() => {
    fetchTutorial();
  }, [projectId]);

  const copyToClipboard = async (text, elementId) => {
    try {
      await navigator.clipboard.writeText(text);
      // Show visual feedback
      const button = document.getElementById(elementId);
      if (button) {
        const originalText = button.innerHTML;
        button.innerHTML = '✓ Copied!';
        button.style.background = '#28a745';
        setTimeout(() => {
          button.innerHTML = originalText;
          button.style.background = '';
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
  };

  const cleanText = (text) => {
    if (!text) return text;
    
    let cleaned = text
      .replace(/\\n/g, '\n')  // Replace \n with actual newlines
      .replace(/\\t/g, '\t')  // Replace \t with actual tabs
      .replace(/\\'/g, "'")   // Replace \' with single quotes
      .replace(/\\"/g, '"')   // Replace \" with double quotes
      .replace(/\\{/g, '{')   // Replace \{ with {
      .replace(/\\}/g, '}')   // Replace \} with }
      .replace(/\\`/g, '`')   // Replace \` with `
      .replace(/\\\\/g, '\\'); // Replace \\ with \
    
    // Try to extract content from JSON structure if it exists
    // Look for the pattern: "content": " and then extract everything after it
    const contentStartMatch = cleaned.match(/"content":\s*"/);
    if (contentStartMatch) {
      // Find where "content": " starts
      const contentStart = contentStartMatch.index + contentStartMatch[0].length;
      // Extract everything after "content": "
      let contentPart = cleaned.substring(contentStart);
      
      // Remove any JSON closing artifacts at the end
      contentPart = contentPart
        .replace(/",?\s*\}[\s\S]*$/g, '')  // Remove trailing ", } and anything after
        .replace(/"\s*\}[\s\S]*$/g, '')    // Remove trailing " } and anything after
        .replace(/"\s*$/g, '')             // Remove trailing quote at end
        .replace(/\}[\s\S]*$/g, '')        // Remove trailing } and anything after
        .replace(/and at the bottom[\s\S]*$/gi, '')  // Remove "and at the bottom" artifacts
        .trim();
      
      cleaned = contentPart;
    } else {
      // No JSON structure found, clean up artifacts
      cleaned = cleaned
        // Remove JSON code blocks
        .replace(/```json[\s\S]*?```/gi, '')
        // Remove standalone JSON objects
        .replace(/\{\s*"title"[\s\S]*?\}/gi, '')
        // Remove partial JSON structures
        .replace(/\{\s*"title"[\s\S]*?"content":\s*"/gi, '')
        .trim();
    }
    
    // Final cleanup of common artifacts
    cleaned = cleaned
      .replace(/and at the bottom\s*"?\s*\}?\s*/gi, '')
      .replace(/"\s*\}\s*```?\s*/g, '')
      .replace(/\}\s*```?\s*/g, '')
      .replace(/```\s*/g, '')
      .trim();
    
    // Aggressive quote removal at the end - keep removing until no more quotes
    while (cleaned.endsWith('"') || cleaned.endsWith('"') || cleaned.endsWith('"')) {
      cleaned = cleaned.slice(0, -1).trim();
    }
    
    // Also remove quotes with any whitespace
    cleaned = cleaned
      .replace(/"\s*$/g, '')
      .replace(/"\s*$/g, '')  // Smart quotes
      .replace(/"\s*$/g, '')  // Smart quotes
      .trim();
    
    return cleaned;
  };

  const fetchTutorial = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_URL}/projects/${projectId}/tutorial`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch tutorial: ${response.status}`);
      }
      
      const tutorialData = await response.json();
      
      // Clean up escape characters in all text fields
      if (tutorialData.title) {
        tutorialData.title = cleanText(tutorialData.title);
      }
      if (tutorialData.meta_description) {
        tutorialData.meta_description = cleanText(tutorialData.meta_description);
      }
      if (tutorialData.content) {
        tutorialData.content = cleanText(tutorialData.content);
        // Fix image URLs to use current API_URL if they're pointing to localhost hardcoded
        tutorialData.content = tutorialData.content.replace(
          /src="http:\/\/localhost:8000/g, 
          `src="${API_URL}`
        );
      }
      
      setTutorial(tutorialData);
      
      // Set page meta information
      if (tutorialData.title) {
        document.title = tutorialData.title;
      }
      
      if (tutorialData.meta_description) {
        // Update or create meta description
        let metaDescription = document.querySelector('meta[name="description"]');
        if (!metaDescription) {
          metaDescription = document.createElement('meta');
          metaDescription.name = 'description';
          document.head.appendChild(metaDescription);
        }
        metaDescription.content = tutorialData.meta_description;
      }
      
    } catch (err) {
      console.error('Error fetching tutorial:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateTutorial = async () => {
    if (!projectId) return;
    
    setIsRegenerating(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/projects/${projectId}/generate-tutorial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to regenerate tutorial: ${response.status}`);
      }
      
      // Tutorial regenerated successfully, fetch the new one
      await fetchTutorial();
      
    } catch (err) {
      console.error('Error regenerating tutorial:', err);
      setError(err.message);
    } finally {
      setIsRegenerating(false);
    }
  };

  const downloadAllImages = async () => {
    if (!tutorial || !tutorial.content) {
      alert('No tutorial content available for image download');
      return;
    }

    setIsDownloadingImages(true);

    try {
      console.log('Starting image download using backend proxy...');

      // Use backend endpoint to download images (avoiding CORS issues)
      const response = await fetch(`${API_URL}/projects/${projectId}/download-tutorial-images`, {
        method: 'GET',
        headers: {
          'Accept': 'application/zip',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to download images: ${response.status} ${response.statusText}. ${errorText}`);
      }

      // Get the zip file as a blob
      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('Received empty ZIP file from server');
      }

      console.log('ZIP file received, size:', blob.size, 'bytes');

      // Create and trigger download
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `tutorial-images-project-${projectId}-${Date.now()}.zip`;

      // Ensure the element is added to DOM for some browsers
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Clean up the object URL
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);

      alert('✅ Download complete! The ZIP file should start downloading shortly.');

    } catch (error) {
      console.error('❌ Error downloading images:', error);

      let errorMessage = `Failed to download images: ${error.message}`;

      if (error.message.includes('404')) {
        errorMessage += '\n\nThis could mean:\n1. No tutorial found for this project\n2. No images found in the tutorial content';
      } else if (error.message.includes('500')) {
        errorMessage += '\n\nServer error occurred while processing images. Please try again.';
      }

      alert(errorMessage);
    } finally {
      setIsDownloadingImages(false);
    }
  };

  const formatCreatedDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  if (isLoading) {
    return (
      <div className="tutorial-page">
        <div className="tutorial-loading">
          <div className="loading-spinner"></div>
          <p>Loading tutorial...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tutorial-page">
        <div className="tutorial-error">
          <h2>Error Loading Tutorial</h2>
          <p>{error}</p>
          <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
            ← Back to Project
          </button>
        </div>
      </div>
    );
  }

  if (!tutorial) {
    return (
      <div className="tutorial-page">
        <div className="tutorial-not-found">
          <h2>Tutorial Not Found</h2>
          <p>This tutorial doesn't exist or hasn't been generated yet.</p>
          <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
            ← Back to Project
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tutorial-page">
      <header className="tutorial-header">
        <div className="tutorial-nav">
          <button onClick={() => navigate(`/project/${projectId}`)} className="back-button">
            ← Back to Project
          </button>
          <div className="tutorial-meta">
            <span className="tutorial-date">
              Created: {formatCreatedDate(tutorial.created_at)}
            </span>
            {tutorial.updated_at !== tutorial.created_at && (
              <span className="tutorial-updated">
                Updated: {formatCreatedDate(tutorial.updated_at)}
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="tutorial-main">
        {/* CRM-friendly metadata boxes */}
        <div className="tutorial-metadata">
          <div className="metadata-box">
            <label className="metadata-label">Title</label>
            <div className="metadata-content">
              <span className="metadata-text">{tutorial.title}</span>
              <button 
                id="copy-title"
                className="copy-button"
                onClick={() => copyToClipboard(tutorial.title, 'copy-title')}
                title="Copy title"
              >
                📋 Copy
              </button>
            </div>
          </div>
          
          {tutorial.meta_description && (
            <div className="metadata-box">
              <label className="metadata-label">Meta Description</label>
              <div className="metadata-content">
                <span className="metadata-text">{tutorial.meta_description}</span>
                <button 
                  id="copy-meta"
                  className="copy-button"
                  onClick={() => copyToClipboard(tutorial.meta_description, 'copy-meta')}
                  title="Copy meta description"
                >
                  📋 Copy
                </button>
              </div>
            </div>
          )}
          
          <div className="metadata-box">
            <label className="metadata-label">Full Article Content</label>
            <div className="metadata-content">
              <span className="metadata-text truncated">
                {tutorial.content.replace(/<[^>]*>/g, '').substring(0, 100)}...
              </span>
              <div className="copy-buttons-group">
                <button 
                  id="copy-content"
                  className="copy-button"
                  onClick={() => copyToClipboard(tutorial.content.replace(/<[^>]*>/g, ''), 'copy-content')}
                  title="Copy full article content (plain text)"
                >
                  📋 Copy Text
                </button>
                <button 
                  id="copy-html"
                  className="copy-button html-copy"
                  onClick={() => copyToClipboard(tutorial.content, 'copy-html')}
                  title="Copy full article content (HTML)"
                >
                  🏷️ Copy HTML
                </button>
              </div>
            </div>
          </div>
        </div>

        <article className="tutorial-article">
          <header className="article-header">
            <h1 className="tutorial-title">{tutorial.title}</h1>
            {tutorial.meta_description && (
              <p className="tutorial-description">{tutorial.meta_description}</p>
            )}
          </header>
          
          <div 
            className="tutorial-content"
            dangerouslySetInnerHTML={{ __html: tutorial.content }}
          />
        </article>
      </main>

      <footer className="tutorial-footer">
        <div className="tutorial-actions">
          <button 
            onClick={() => navigate(`/project/${projectId}`)} 
            className="action-button primary"
          >
            Edit Project
          </button>
          <button 
            onClick={handleRegenerateTutorial}
            disabled={isRegenerating}
            className="action-button regenerate"
          >
            {isRegenerating ? '🔄 Regenerating...' : '🔄 Regenerate Tutorial'}
          </button>
          <button 
            onClick={downloadAllImages}
            disabled={isDownloadingImages}
            className="action-button download"
          >
            {isDownloadingImages ? '📦 Downloading...' : '📦 Download Images'}
          </button>
          <button 
            onClick={() => window.print()} 
            className="action-button secondary"
          >
            Print Tutorial
          </button>
        </div>
      </footer>
    </div>
  );
}

export default TutorialPage;